import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { EyeOutlined } from '@ant-design/icons';
import { Table as AntdTable, Button } from 'antd';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import { Flex } from 'ui';
import { withShadow } from 'ui/styles';
// gantt imports
import { ItemTypes } from 'common/gantt/config';
import Draggable from 'common/gantt/components/dnd/containers/dnd-draggable-container';

import TableCell from './table-cell';

const HeaderTitle = styled(Flex)`
  min-width: 300px;
  font-size: 1rem;
  ${p => p.marginLeft && `margin-left: ${p.marginLeft}px;`}
`;

const StyledInput = styled.input`
  margin-top: 19px;
  margin-left: 7px;
`;

const RemainingItems = styled.span`
  margin-left: 10px;
  color: #999;
`;

const StyledTable = styled(AntdTable)`
  .ant-table {
    ${p => p.bordered || withShadow};
  }
  .ant-table,
  .ant-table-footer .ant-btn {
    border-collapse: collapse;
    margin-bottom: 1px;
  }

  .ant-table table {
    border-collapse: collapse !important;
  }

  .ant-table-thead > tr > th {
    border-bottom: 1px solid ${p => p.theme.colors.border};
    background-color: transparent;
    text-transform: uppercase;
  }

  .ant-table-content,
  .ant-table-fixed tbody {
    background-color: ${p => p.theme.colors.table.alternating};
  }

  .ant-table-title {
    color: #fff;
    font-weight: 400;
    background-color: ${p => p.theme.colors.themeSecondary};
    padding: 0.5rem 1rem !important;
    top: 0;
  }

  .ant-table-row:hover {
    cursor: ${p => p.hoverPointer && 'pointer'} !important;
    background-color: ${p => p.hoverPointer && '#e6f9ff'}!important;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    border-right: none;
    padding: 0.35rem 0.5rem 0.5rem 0.5rem !important;
    transition: none !important;
  }

  .ant-table-measure-row {
    display: none;
  }

  .ant-table-placeholder {
    background-color: #fff;
  }

  tr.ant-table-row-level-0.selected,
  tr.ant-table-row-level-0.highlighted {
    border-right: none !important;
  }

  .ant-table-row:nth-child(even) {
    background-color: ${p =>
    p.shouldCalculateAutoWidth ? p.theme.colors.table.alternating : '#fff'};
  }

  .ant-table-row:nth-child(odd) {
    background-color: ${p =>
    p.shouldCalculateAutoWidth ? '#fff' : p.theme.colors.table.alternating};
  }

  .ant-table-cell-fix-left {
    background-color: inherit;
  }

  .ant-table-cell-fix-left-last {
    left: ${p => p.rowSelection && '86px'} !important;
  }
  .ant-table-cell-fix-left:nth-child(2) {
    left: 70px !important;
  }

  .ant-table-tbody > tr:hover > td {
    background: inherit;
  }

  .ant-table-footer {
    background-color: #fff;
  }

  .active {
    background-color: #fff !important;
  }

  .active td {
    background-color: ${p => p.theme.colors.table.active} !important;
  }

  .active:hover td {
    background-color: ${p => p.theme.colors.table.active} !important;
  }

  .inactive td {
    background-color: ${p => p.theme.colors.table.inactive} !important;
  }

  .inactive:hover td {
    background-color: ${p => p.theme.colors.table.inactive} !important;
  }

  .ant-table-empty .ant-table-placeholder {
    font-size: ${p => p.theme.fontSize.large};
    padding: 0.75rem;
    z-index: 0;
  }

  .selected {
    border: 2px solid ${p => p.theme.colors.theme};
  }

  .highlighted {
    border: 2px solid ${p => p.theme.colors.theme};
  }

  .ant-table-selection-column .ant-checkbox-disabled {
    display: none !important;
  }

  .ant-table-fixed-left {
    z-index: 1;
  }

  .ant-checkbox {
    width: 60px !important;
    height: 60px !important;
    display: flex !important;
    &::after {
      border: none !important;
    }
    .ant-checkbox-inner {
      margin: auto !important;
    }
  }
  `;

const Table = ({
  columns,
  data,
  isLoading,
  onRowSelect,
  onRowClick,
  selectedRowKeys,
  getCheckboxProps,
  headerTitle,
  pageSize = 10,
  toolbar,
  hasFooter = true,
  rowKey = 'key',
  activeRow = {},
  hasRowSelector = true,
  customFooter,
  customMoreFunction,
  customRemainingItems,
  customMoreCount,
  scroll,
  intl,
  bordered = false,
  onToggleAll,
  shouldCalculateAutoWidth = false,
  highlightedKeys = [],
  isPlanningBoard = false,
  isToggleAllSelected,
  showAllRecords = false,
  expandable = null,
  className = '',
}) => {
  const [currentPageSize, setCurrentPageSize] = useState(data?.length > 0 && data?.length < pageSize
    ? data.length
    : pageSize);

  useEffect(() => {
    setCurrentPageSize(data?.length > 0 && data?.length < pageSize
      ? data?.length
      : pageSize);
  }, [data?.length, pageSize]);

  const getRemainingItems = () => {
    if (customRemainingItems) {
      return customRemainingItems();
    }
    return data?.length - currentPageSize;
  };

  const getMoreItemsToShow = () => {
    const remaining = getRemainingItems();
    if (customMoreCount) {
      if (customMoreCount <= remaining) {
        return customMoreCount;
      }
      return remaining;
    }
    if (remaining > 0 && pageSize <= remaining) {
      return pageSize;
    }
    return remaining;
  };

  const pluralize = (count, text) => count > 1 ? `${text}s` : text;

  const defaultCellRender = (column, value, record, index) => {
    let colValue = value;
    // TEMP fix, until new worklist is implemented everywhere
    // TODO: remove this once we use new worklist everywhere
    if (typeof colValue === 'object') {
      if (Array.isArray(colValue)) {
        colValue = colValue?.map(v => v?.description).join('; ');
      } else {
        colValue = value?.description;
      }
    }
    if (isPlanningBoard) {
      const pbData = {
        linkedEntityId: record.id,
        linkedEntityType: 'order',
      };
      return (
        <Draggable
          hasDragStyle={false}
          data={{
            ...pbData,
            canMove: record?.actions?.find(a => a.id === 'drag'),
            isAssignedPlanner: true,
          }}
          type={ItemTypes.ORDER}
        >
          <TableCell {...column} value={colValue} record={record} index={index} />
        </Draggable>
      );
    }
    return <TableCell {...column} value={colValue} record={record} index={index} />;
  };

  const getColumnsWithDefaultRender = () =>
    columns?.map((column) => {
      const { label, dataIndex, onCell, render, width, fixed = false, ellipsis = true } = column;
      return {
        title: label,
        dataIndex,
        onCell,
        key: dataIndex || `key${Math.random()}`,
        width,
        render: render || defaultCellRender.bind(null, column),
        fixed,
        ellipsis,
      };
    });

  const showMoreItems = () => setCurrentPageSize(s => s + getMoreItemsToShow());

  const getFooter = () => {
    if (!hasFooter) {
      return null;
    }
    if (customFooter) {
      return customFooter;
    }
    if (getRemainingItems() <= 0) {
      return null;
    }
    return () =>
      <>
        <Button icon={<EyeOutlined />} onClick={customMoreFunction || showMoreItems}>
          {`Show ${getMoreItemsToShow()} more ${pluralize(
            getMoreItemsToShow(),
            'item',
          )}`}
        </Button>

        <RemainingItems>
          {`(Remaining: ${getRemainingItems()} ${pluralize(
            getRemainingItems(),
            'item',
          )})`}
        </RemainingItems>
      </>;
  };

  const getRowStyles = (record) => {
    let rowStyle = '';
    if (selectedRowKeys && selectedRowKeys.length) {
      if (selectedRowKeys.includes(record.key) || selectedRowKeys.includes(record.id)) {
        rowStyle = 'selected';
      }
    }
    if (highlightedKeys && highlightedKeys.length) {
      if (highlightedKeys.includes(record.key) || highlightedKeys.includes(record.id)) {
        rowStyle = 'highlighted';
      }
    }
    if (activeRow.id && record[activeRow.rowField] === activeRow.id) {
      rowStyle += ' active';
    }

    if (record && record.type && record.type === 'historicalorder') {
      rowStyle += ' inactive';
    }

    return rowStyle;
  };

  const getTotalWidth = (cols) => {
    let width = 0;
    if (shouldCalculateAutoWidth) {
      cols.forEach(
        element => (width += element.width ? parseInt(element.width.replace('px', ''), 10) : 0),
      );
      // Add the checkbox width
      if (hasRowSelector) {
        width += 48;
      }
      // Add the padding
      width += 16 * cols.length;
      return scroll && scroll.y ? { x: width, y: scroll.y } : { x: width };
    }
    return scroll || {};
  };

  const toggleAll = (e) => {
    const { checked } = e.target;
    if (!onToggleAll) {
      return;
    }
    if (checked) {
      onToggleAll(data?.map(d => d.id));
    } else {
      onToggleAll([]);
    }
  };

  const title = () => {
    if (onToggleAll) {
      return (
        <Flex>
          <StyledInput
            type='checkbox'
            onClick={toggleAll}
            checked={isToggleAllSelected}
          />
          <HeaderTitle marginLeft={30} fdc jcc>
            {headerTitle}
          </HeaderTitle>
          {toolbar && <>{toolbar}</>}
        </Flex>
      );
    }
    return (
      <Flex>
        <HeaderTitle fdc jcc>
          {headerTitle}
        </HeaderTitle>
        {toolbar && <>{toolbar}</>}
      </Flex>
    );
  };

  const columnsDefaultRender = getColumnsWithDefaultRender();
  return (
    <StyledTable
      data-test-id='styled-table'
      scroll={getTotalWidth(columnsDefaultRender)}
      shouldCalculateAutoWidth={shouldCalculateAutoWidth}
      bordered={bordered}
      pagination={false}
      showHeader={false}
      loading={isLoading}
      rowClassName={getRowStyles}
      rowSelection={
        hasRowSelector ? { onChange: onRowSelect, selectedRowKeys, getCheckboxProps } : null
      }
      columns={columnsDefaultRender}
      dataSource={showAllRecords ? data : data?.filter((v, i) => i < currentPageSize)}
      locale={{ emptyText: intl.formatMessage({ id: 'table.NO_DATA' }) }}
      title={headerTitle && title}
      footer={getFooter()}
      rowKey={rowKey}
      onRow={row => ({
        onClick: () => onRowClick && onRowClick(row),
      })}
      hoverPointer={onRowClick || false}
      tableLayout='auto'
      expandable={expandable || {
        showExpandColumn: false,
        rowExpandable: false,
      }}
      childrenColumnName='ColumnNameThatWillNeverBeUsed'
      className={className}
    />
  );
};

Table.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onRowSelect: PropTypes.func,
  onRowClick: PropTypes.func,
  selectedRowKeys: PropTypes.array,
  getCheckboxProps: PropTypes.func,
  headerTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  pageSize: PropTypes.number,
  toolbar: PropTypes.node,
  hasFooter: PropTypes.bool,
  rowKey: PropTypes.string,
  activeRow: PropTypes.shape({
    id: PropTypes.string,
    rowField: PropTypes.string,
  }),
  hasRowSelector: PropTypes.bool,
  customFooter: PropTypes.func,
  customMoreFunction: PropTypes.func,
  customRemainingItems: PropTypes.func,
  customMoreCount: PropTypes.number,
  scroll: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  intl: PropTypes.object,
  bordered: PropTypes.bool,
  onToggleAll: PropTypes.func,
  shouldCalculateAutoWidth: PropTypes.bool,
  highlightedKeys: PropTypes.array,
  isPlanningBoard: PropTypes.bool,
  isToggleAllSelected: PropTypes.bool,
  showAllRecords: PropTypes.bool,
  expandable: PropTypes.object,
  className: PropTypes.string,
};

export default injectIntl(Table);
